import { environment } from '@environment';
import { BehaviorSubject, defer, iif, Observable, Subject, throwError, timer } from 'rxjs';
import { catchError, concatMap, distinctUntilChanged, retryWhen, tap } from 'rxjs/operators';
import { webSocket, WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';
import { Logger } from '@mona/shared/logger';
// Relative imports — avoid @mona/shared/utils barrel (circular + pulls tokens/logger graph)
import { AppError, BaseErrors } from '../helpers/errors.model';
import { isEmpty, isEmptyObject } from '../types/type-guards';

/**
 * Ws service
 */
export class WsExtendableService<T, R = unknown> {
    /** Socket url */
    originalUrl: string;
    /**
     * Connection status observer
     */
    private _connection$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /**
     * Connection status observer
     */
    get connection$(): Observable<boolean> {
        return this._connection$.pipe(distinctUntilChanged());
    }

    /**
     * Connection status observer - sync
     */
    get connection(): boolean {
        return this._connection$.getValue();
    }
    set connection(value: boolean) {
        this._connection$.next(value);
    }
    /**
     * Subject which emits ws errors
     */
    private _wsError$: Subject<AppError> = new Subject();
    /**
     * Public subject which emits ws errors
     */
    get wsError$(): Observable<AppError> {
        return this._wsError$.asObservable();
    }
    /**
     * Attempts done after failing
     */
    readonly reconnectConfig = {
        attempts: environment.reconnectAttempts,
        interval: environment.reconnectInterval,
        count: 0,
    };
    /**
     * private WebSocketSubject config
     */
    private _config: WebSocketSubjectConfig<T>;
    /**
     * WebSocketSubject config
     */
    get config(): WebSocketSubjectConfig<T> {
        return this._config;
    }
    /**
     * WebSocketSubject config defaults. closeObserver must NOT unsubscribe
     * `this.websocket$` — onclose of a replaced socket would tear down the new one
     * and leave the old native WS looking like a stale 101 in DevTools.
     */
    private defaultWsConfig: Partial<WebSocketSubjectConfig<T>> = {
        WebSocketCtor: WebSocket,
        closeObserver: {
            next: event => {
                // Structured close event log. The close code is the single most useful piece
                // of info when diagnosing WS problems in production:
                //   1000 = normal closure               (server/app initiated, clean)
                //   1001 = going away                   (page navigation, device reload)
                //   1005 = no status received           (abnormal, typically proxy tore-down)
                //   1006 = abnormal closure             (nginx upstream reset, device offline)
                //   1011 = server error                 (backend crash, OOM)
                //   1012 = service restart              (backend rolling restart)
                //   1013 = try again later              (backend overloaded / 502-equivalent)
                // 4xxx  = application-defined           (auth rejected, encounter mismatch, …)
                this.logger.log(
                    `[ws] ${this.constructor.name} close url=${this.originalUrl} ` +
                        `code=${event.code} wasClean=${event.wasClean} ` +
                        `reason=${JSON.stringify(event.reason || '')} ` +
                        `reconnectAttempts=${this.reconnectConfig.count}/${this.reconnectConfig.attempts}`,
                );
                this.connection = false;
            },
        },
        openObserver: {
            next: () => {
                this.connection = true;
                this.logger.log(
                    `[ws] ${this.constructor.name} open url=${this.originalUrl} ` +
                        `afterReconnectAttempts=${this.reconnectConfig.count}`,
                );
                // Reset the retry counter once we're connected again so the next
                // disconnect/reconnect cycle gets the full retry budget.
                this.reconnectConfig.count = 0;
            },
        },
    };

    /**
     * Web socket subject holder
     */
    protected websocket$: WebSocketSubject<T>;

    /**
     * Ws messages
     */
    readonly wsMessages$ = new Subject<T>();

    protected readonly logger = new Logger();

    /**
     * Set websocket service config
     * @param url
     * @param accessToken
     * @param wsConfig
     * @param reconnectInterval
     * @param reconnectAttempts
     */
    setConfig(
        url: string,
        accessToken = '',
        wsConfig: Partial<WebSocketSubjectConfig<T>> = {},
        reconnectInterval?: number,
        reconnectAttempts?: number,
    ) {
        try {
            this.originalUrl = new URL(url).href.slice(0, -1);
        } catch (error) {
            throw new Error('Invalid URL for WebSocket');
        }
        if (accessToken) {
            url = url.concat(`?access_token=${accessToken}`);
        }
        // Fresh config object — never mutate defaultWsConfig (shared close/open observers)
        this._config = {
            ...this.defaultWsConfig,
            url,
            ...wsConfig,
        };
        // Only override when callers pass values — DataUpdates/Notifications call setConfig(url, token)
        // and used to clobber attempts to undefined, killing internal reconnect during downtime.
        if (reconnectInterval != null) {
            this.reconnectConfig.interval = reconnectInterval;
        }
        if (reconnectAttempts != null) {
            this.reconnectConfig.attempts = reconnectAttempts;
        }
        this.reconnectConfig.count = 0;
    }

    /**
     * Disconnect — closes the native WebSocket (OPEN or CONNECTING).
     * Prefer unsubscribe() over complete(): complete() only closes after onopen has wired
     * the destination; unsubscribe() closes readyState 0 and 1.
     */
    disconnect(): void {
        this.connection = false;
        const ws = this.websocket$;
        this.websocket$ = null;
        if (!ws) {
            return;
        }
        try {
            ws.unsubscribe();
        } catch {
            // already closed
        }
    }

    /**
     * Connect to WebSocked
     * @param args
     */
    connect(...args: any): Observable<T | R> {
        if (isEmptyObject(this.config)) {
            throw new Error(`${this.constructor.name} can not connect without proper config`);
        }
        this.logger.log(`${this.constructor.name} try connect to`, this.originalUrl);
        // Reuse only a live subject. After unclean close / last-subscriber teardown, a closed
        // leftover must not be returned (CallWsService and similar call connect() without
        // disconnect() first).
        if (this.websocket$?.closed) {
            this.websocket$ = null;
        }
        if (this.websocket$) {
            return this.websocket$.asObservable() as Observable<any>;
        }

        this.websocket$ = webSocket(this.config);
        const failedSocket = this.websocket$;
        return this.websocket$.pipe(
            this.reconnect(),
            catchError(wsError => {
                this._wsError$.next(wsError);
                // Enhanced error log: the original implementation only emitted the message,
                // which in the nginx-tore-down-upstream case is literally the string
                // "Unknown error" — not enough to diagnose. Emit structured fields instead
                // (url, reconnect state, plain JSON dump of originalError) so production
                // logs can be correlated with upstream nginx/api failures.
                const originalMessage = wsError?.originalError?.message ?? String(wsError?.originalError ?? wsError);
                this.logger.error(
                    `[ws] ${this.constructor.name} error url=${this.originalUrl} ` +
                        `attempts=${this.reconnectConfig.count}/${this.reconnectConfig.attempts} ` +
                        `message=${JSON.stringify(originalMessage)}`,
                );
                // Only tear down if this error belongs to the current socket. Reconnect flows
                // disconnect+connect quickly; a late close from the old socket must not
                // disconnect() the replacement (leaves UI subscribed to a dead stream).
                if (this.websocket$ === failedSocket) {
                    this.disconnect();
                }

                if (wsError instanceof AppError) {
                    return throwError(wsError);
                }

                return throwError({
                    errorCode: BaseErrors.WEBSOCKET,
                    originalError: {
                        message: 'Websocket error has happened. Check terminal logs for more details.',
                        wsError,
                    },
                });
            }),
            tap(message => {
                this.wsMessages$.next(message);
            }),
        );
    }

    /**
     * Send message trough websocket, serialize by default subject serializer
     * @param message
     */
    send(message: T) {
        if (this.websocket$ && !isEmpty(message)) {
            this.websocket$ && this.websocket$.next(message);
        }
    }

    /**
     * Retry a given observable by a time span
     */
    private reconnect() {
        const {
            reconnectConfig: _reconnect,
            constructor: { name },
            logger: { log },
        } = this;
        return (source: Observable<any>) => {
            if (!_reconnect.attempts) {
                return source;
            }
            return defer(() => {
                return source.pipe(
                    retryWhen<T>(errors =>
                        errors.pipe(
                            concatMap((error: CloseEvent, index) => {
                                _reconnect.count = index + 1;
                                // Structured reconnect log — includes the underlying close
                                // code so we can tell a transient upstream failure (1006)
                                // from a deliberate server-side close (1000/1001).
                                log(
                                    `[ws] ${name} reconnect attempt #${_reconnect.count}/${_reconnect.attempts} ` +
                                        `in ${_reconnect.interval}ms ` +
                                        `prevCloseCode=${error?.code ?? 'n/a'} ` +
                                        `prevCloseReason=${JSON.stringify(error?.reason || '')}`,
                                );
                                return iif(
                                    () => _reconnect.count < _reconnect.attempts,
                                    timer(_reconnect.interval),
                                    throwError(
                                        new AppError({
                                            message: 'Maximum reconnect attempts',
                                            code: BaseErrors.WEBSOCKET_MAX_RETRIES,
                                            status: error.code,
                                        }),
                                    ),
                                );
                            }),
                        ),
                    ),
                );
            });
        };
    }
}
